import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, SITE_URL } from "@/lib/api";
import { orgGraph, providerRef, webPage } from "@/lib/schema";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import LeadForm from "@/components/LeadForm";
import CtaBlock from "@/components/CtaBlock";
import NotFound from "@/pages/NotFound";
import { useCompany } from "@/context/CompanyContext";
import { CheckCircle2, ArrowRight, AlertTriangle, ListChecks, Sparkles, Phone } from "lucide-react";
import { trackEvent } from "@/lib/ga";

const CORE_COMBO = ["ac-repair", "ac-installation", "ac-maintenance", "heating-repair", "heat-pumps", "indoor-air-quality"];
const WARRANTY_SLUGS = ["ac-installation", "ac-replacement", "heating-installation", "heat-pumps"];

function Loading() {
  return <div className="max-w-7xl mx-auto px-4 py-24 text-slate-400" data-testid="service-loading">Loading…</div>;
}

export default function ServicePage() {
  const { slug } = useParams();
  const { company } = useCompany();
  const { data: service, isLoading, isError } = useQuery({
    queryKey: ["service", slug],
    queryFn: async () => (await api.get(`/services/${slug}`)).data,
    retry: false,
  });
  const { data: allServices = [] } = useQuery({ queryKey: ["all-services"], queryFn: async () => (await api.get("/services")).data });
  const { data: allLocations = [] } = useQuery({ queryKey: ["all-locations-svc"], queryFn: async () => (await api.get("/locations")).data });
  const { data: blog = [] } = useQuery({ queryKey: ["all-blog"], queryFn: async () => (await api.get("/blog")).data });

  if (isLoading) return <Loading />;
  if (isError || !service) return <NotFound />;

  const related = (service.related_services || []).map((s) => allServices.find((x) => x.slug === s)).filter(Boolean);
  const relatedArticles = blog.filter((b) => (b.related_services || []).includes(service.slug)).slice(0, 3);

  const jsonLd = [
    ...orgGraph(company),
    webPage(`/${service.slug}/`, service.seo_title || service.name, service.meta_description),
    {
      "@context": "https://schema.org", "@type": "Service",
      name: service.name, serviceType: service.name,
      description: service.meta_description,
      provider: providerRef(),
      areaServed: company.primary_service_area,
      url: `${SITE_URL}/${service.slug}/`,
    },
  ];

  return (
    <>
      <Seo title={service.seo_title} description={service.meta_description} path={`/${service.slug}/`} image={service.image} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative bg-navy text-white" data-testid="service-hero">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.image_alt} className="w-full h-full object-cover opacity-30" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pb-20">
          <div className="[&_*]:!text-slate-300">
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: service.name }]} />
          </div>
          <h1 className="mt-6 max-w-3xl text-4xl sm:text-5xl font-extrabold tracking-tight">{service.h1}</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 leading-relaxed">{service.intro}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("phone_click", { location: "service_hero", service: service.slug })} data-testid="service-call" className="inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 px-7 rounded-md transition-colors"><Phone className="h-5 w-5" /> Call {company.phone}</a>
            <a href="#service-quote" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-3 px-7 rounded-md transition-colors">Request Service <ArrowRight className="h-5 w-5" /></a>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 prose-launch max-w-none">
            <p className="text-lg leading-relaxed text-slate-700">{service.explanation}</p>

            <h2 className="flex items-center gap-2"><AlertTriangle className="h-6 w-6 text-launch" /> Signs You May Need This Service</h2>
            <ul>
              {(service.signs || []).map((s, i) => <li key={i}>{s}</li>)}
            </ul>

            <h2 className="flex items-center gap-2"><Sparkles className="h-6 w-6 text-cool" /> Benefits of Professional Service</h2>
            <ul>
              {(service.benefits || []).map((b, i) => <li key={i}>{b}</li>)}
            </ul>

            <h2 className="flex items-center gap-2"><ListChecks className="h-6 w-6 text-navy" /> Our Process</h2>
            <ol>
              {(service.process || []).map((p, i) => <li key={i}>{p}</li>)}
            </ol>

            <div className="not-prose mt-8 bg-navy rounded-xl p-6 sm:p-8 text-white dot-grid">
              <h3 className="text-2xl font-bold">Why Choose {company.name}?</h3>
              <p className="mt-2 !text-slate-200">As your local Space Coast experts, we bring mission-grade care to every job — licensed, insured, upfront on pricing, and backed by a satisfaction guarantee.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                {["Licensed & insured technicians", "Same-day & 24/7 emergency service", "Upfront, honest pricing", "All makes & models serviced"].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-sm text-slate-200"><CheckCircle2 className="h-4 w-4 text-cool" /> {t}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-6">
            <div id="service-quote" className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:sticky lg:top-24">
              <h3 className="text-xl font-bold text-navy">Request {service.name}</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">Fast response. No obligation.</p>
              <LeadForm defaultService={service.name} compact />
            </div>
            {related.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-navy mb-3">Related Services</h3>
                <ul className="space-y-2">
                  {related.map((r) => (
                    <li key={r.slug}><Link to={`/${r.slug}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-launch transition-colors" data-testid={`related-service-${r.slug}`}><ArrowRight className="h-4 w-4 text-launch" /> {r.name}</Link></li>
                  ))}
                </ul>
              </div>
            )}
            {relatedArticles.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-navy mb-3">Helpful Reading</h3>
                <ul className="space-y-3">
                  {relatedArticles.map((a) => (
                    <li key={a.slug}><Link to={`/blog/${a.slug}`} className="text-sm text-slate-700 hover:text-launch transition-colors" data-testid={`related-article-${a.slug}`}>{a.title}</Link></li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      {CORE_COMBO.includes(service.slug) && allLocations.length > 0 && (
        <section className="bg-white border-t border-slate-200 py-14" data-testid="service-city-grid">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-navy">{service.name} Across Brevard County</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">We bring {service.name.toLowerCase()} to homes throughout the Space Coast. Choose your city for local details.</p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {allLocations.map((l) => (
                <Link key={l.slug} to={`/${service.slug}/${l.slug}`} data-testid={`service-city-${l.slug}`} className="group flex items-center justify-between bg-slate-50 hover:bg-white border border-slate-200 hover:border-cool rounded-lg px-4 py-3 transition-colors">
                  <span className="font-semibold text-navy text-sm">{service.name} in {l.city}, FL</span>
                  <ArrowRight className="h-4 w-4 text-launch group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {WARRANTY_SLUGS.includes(service.slug) && (
        <section className="bg-navy text-white dot-grid py-14" data-testid="service-warranty-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><CheckCircle2 className="h-4 w-4" /> Backed for the Long Haul</p>
              <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">Ask About Our Lifetime Warranty</h2>
              <p className="mt-2 !text-slate-200 text-slate-200">Florida's first HVAC company offering a Lifetime Warranty. Qualifying systems we install may be eligible for additional Lifetime Warranty protection — ask our team what's available for your home.</p>
            </div>
            <Link to="/lifetime-warranty" data-testid="service-warranty-link" className="inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 px-7 rounded-md shadow-lg transition-colors shrink-0">
              Explore the Lifetime Warranty <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>
      )}

      <div className="bg-slate-100"><FaqAccordion faqs={service.faqs} /></div>
      <CtaBlock heading={`Need ${service.name}? We're Ready for Liftoff.`} />
    </>
  );
}
